import {
  useRef,
  useCallback,
  useState,
  type PointerEventHandler,
  type KeyboardEvent,
} from 'react'
import cx from 'classnames'
import {
  InputShellRoot,
  InputShellControl,
} from '@/components/inputs/InputShell'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { useFocus } from 'react-aria'
import { useNumericInput } from '@/hooks/numeric'
import styles from './NumericQuestionAnswerEditor.module.css'
import type { Observable } from 'rxjs'
import { useObservable } from '@/hooks/rx-hooks'

export interface NumericQuestionAnswerEditorProps {
  value?: number | null
  onChange?: (value: number | null) => void
  precision?: number
  isDisabled?: boolean
  isInvalid?: boolean
  isSkeleton?: boolean
  placeholder?: string
  /**
   * Commit the numeric value when called.
   */
  commit$?: Observable<unknown>
  name?: string
  'aria-describedby'?: string
  'aria-labelledby'?: string
  /**
   * Test ID attribute for the numeric input.
   */
  testId?: string
}

export function NumericQuestionAnswerEditor({
  value,
  onChange,
  precision = 2,
  isDisabled,
  isInvalid,
  isSkeleton,
  commit$,
  placeholder = 'Enter a number',
  name,
  'aria-describedby': ariaDescribedby,
  'aria-labelledby': ariaLabelledby,
  testId,
}: NumericQuestionAnswerEditorProps) {
  const isSpacious = useIsSpacious()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  const numericConnector = useNumericInput({
    value,
    onChange,
    precision,
    mode: 'draft',
  })

  const { commit, keyDownPreventNonNumeric } = numericConnector
  useObservable(commit$, commit)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      keyDownPreventNonNumeric(e)
      if (e.key === 'Enter') {
        commit()
      }
    },
    [commit, keyDownPreventNonNumeric],
  )

  const handleBlur = useCallback(() => {
    commit()
  }, [commit])

  const focusInputOnClick: PointerEventHandler = useCallback((event) => {
    const target = event.target as HTMLElement
    if (target.closest('input, button, a')) {
      return
    }

    const input = inputRef.current
    if (!input) {
      return
    }

    requestAnimationFrame(() => {
      input.focus()
    })
  }, [])

  const { focusProps } = useFocus({
    onFocusChange: setIsFocused,
    onBlur: handleBlur,
  })

  return (
    <InputShellRoot
      isFocused={isFocused}
      onPointerDown={focusInputOnClick}
      isDisabled={isDisabled}
      isSkeleton={isSkeleton}
      invalid={isInvalid}
    >
      <InputShellControl pad>
        <div className={styles.control}>
          <input
            name={name}
            ref={inputRef}
            disabled={isDisabled}
            type="text"
            inputMode={precision === 0 ? 'numeric' : 'decimal'}
            value={numericConnector.value}
            onChange={(e) => numericConnector.onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-describedby={ariaDescribedby}
            aria-labelledby={ariaLabelledby}
            data-test={testId}
            {...focusProps}
            placeholder={placeholder}
            className={cx(
              styles.input,
              isSpacious ? styles.inputSpacious : styles.inputDefault,
              isDisabled && styles.isDisabled,
            )}
          />
        </div>
      </InputShellControl>
    </InputShellRoot>
  )
}
